import React, { useEffect, useState } from "react";
import { useAuth } from "../components/AuthContext";

export default function SecuritySettings() {
  const { adminEmail } = useAuth();
  const [activeTab, setActiveTab] = useState('password');
  const [requirements, setRequirements] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [saving, setSaving] = useState(false);

  const headers = { 'x-admin-email': adminEmail };

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [reqRes, histRes, sessRes] = await Promise.all([
        fetch('/api/security/password-requirements').then(r => r.json()),
        fetch('/api/security/login-history', { headers }).then(r => r.json()),
        fetch('/api/security/sessions', { headers }).then(r => r.json())
      ]);
      setRequirements(reqRes.requirements || []);
      setLoginHistory(histRes.history || []);
      setSessions(sessRes.sessions || []);
    } catch (e) {
      console.error('Load error:', e);
    } finally {
      setLoading(false);
    }
  }

  async function validatePasswordLive(password) {
    try {
      const res = await fetch('/api/security/validate-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      setPasswordErrors(data.errors || []);
      
      // Calculate strength
      let strength = 0;
      if (password.length >= 8) strength += 20;
      if (password.length >= 12) strength += 10;
      if (/[A-Z]/.test(password)) strength += 20;
      if (/[a-z]/.test(password)) strength += 20;
      if (/[0-9]/.test(password)) strength += 15;
      if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 15;
      setPasswordStrength(Math.min(100, strength));
    } catch (e) {
      console.error('Validation error:', e);
    }
  }

  async function changePassword() {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    
    if (passwordErrors.length > 0) {
      alert('Please fix password requirements');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch('/api/security/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: adminEmail,
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });
      const data = await res.json();
      
      if (data.error) throw new Error(data.error);
      
      alert('Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordStrength(0);
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  function getStrengthColor() {
    if (passwordStrength >= 80) return '#10b981';
    if (passwordStrength >= 60) return '#f59e0b';
    if (passwordStrength >= 40) return '#f97316';
    return '#ef4444';
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 20 }}>
      <h1 style={{ margin: '0 0 8px 0', fontSize: 28, fontWeight: 900 }}>🔒 Security Settings</h1>
      <p style={{ margin: '0 0 24px 0', color: '#6b7280' }}>Manage your account security and password</p>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, borderBottom: '2px solid #e5e7eb', paddingBottom: 8 }}>
        {[
          { id: 'password', label: '🔑 Password' },
          { id: 'history', label: '📋 Login History' },
          { id: 'sessions', label: '💻 Sessions' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px',
              background: activeTab === tab.id ? '#dc2626' : 'transparent',
              color: activeTab === tab.id ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: 8,
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Password Tab */}
      {activeTab === 'password' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24 }}>
            <h3 style={{ margin: '0 0 20px 0' }}>Change Password</h3>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Current Password</label>
              <input 
                type="password" 
                value={passwordForm.currentPassword} 
                onChange={e => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e5e7eb' }}
              />
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>New Password</label>
              <input 
                type="password" 
                value={passwordForm.newPassword} 
                onChange={e => {
                  setPasswordForm({ ...passwordForm, newPassword: e.target.value });
                  validatePasswordLive(e.target.value);
                }}
                style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e5e7eb' }}
              />
              
              {/* Strength Meter */}
              {passwordForm.newPassword && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                    <span>Password Strength</span>
                    <span style={{ color: getStrengthColor() }}>
                      {passwordStrength >= 80 ? 'Strong' : passwordStrength >= 60 ? 'Good' : passwordStrength >= 40 ? 'Fair' : 'Weak'}
                    </span>
                  </div>
                  <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3 }}>
                    <div style={{ height: '100%', width: `${passwordStrength}%`, background: getStrengthColor(), borderRadius: 3, transition: 'all 0.3s' }} />
                  </div>
                </div>
              )}
            </div>
            
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Confirm New Password</label>
              <input 
                type="password" 
                value={passwordForm.confirmPassword} 
                onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                style={{ 
                  width: '100%', 
                  padding: 10, 
                  borderRadius: 6, 
                  border: `1px solid ${passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword ? '#ef4444' : '#e5e7eb'}` 
                }}
              />
              {passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
                <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>Passwords do not match</div>
              )}
            </div>
            
            <button 
              onClick={changePassword} 
              disabled={saving || passwordErrors.length > 0}
              style={{ 
                width: '100%', 
                padding: 12, 
                background: saving || passwordErrors.length > 0 ? '#9ca3af' : '#dc2626', 
                color: 'white', 
                border: 'none', 
                borderRadius: 8, 
                fontWeight: 700, 
                cursor: saving ? 'not-allowed' : 'pointer' 
              }}
            >
              {saving ? 'Changing...' : 'Change Password'}
            </button>
          </div>
          
          {/* Requirements */}
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24 }}>
            <h3 style={{ margin: '0 0 20px 0' }}>Password Requirements</h3>
            <div style={{ display: 'grid', gap: 12 }}>
              {requirements.map((req, i) => {
                const met = passwordForm.newPassword && !passwordErrors.some(e => e.toLowerCase().includes(req.toLowerCase().split(' ')[2]));
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ 
                      width: 24, 
                      height: 24, 
                      borderRadius: '50%', 
                      background: met ? '#d1fae5' : '#f3f4f6',
                      color: met ? '#10b981' : '#9ca3af',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14
                    }}>
                      {met ? '✓' : '○'}
                    </span>
                    <span style={{ color: met ? '#10b981' : '#6b7280' }}>{req}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Login History Tab */}
      {activeTab === 'history' && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '100px 120px 150px 1fr', gap: 12, padding: 16, background: '#f9fafb', fontWeight: 700, fontSize: 12, color: '#6b7280' }}>
            <div>Status</div>
            <div>Time</div>
            <div>IP Address</div>
            <div>Details</div>
          </div>
          {loginHistory.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No login history available</div>
          ) : (
            loginHistory.map((entry, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '100px 120px 150px 1fr', gap: 12, padding: 16, borderTop: '1px solid #f3f4f6', alignItems: 'center' }}>
                <div>
                  <span style={{ 
                    padding: '4px 8px', 
                    borderRadius: 4, 
                    fontSize: 11, 
                    fontWeight: 600,
                    background: entry.action === 'LOGIN' ? '#d1fae5' : '#fee2e2',
                    color: entry.action === 'LOGIN' ? '#065f46' : '#dc2626'
                  }}>
                    {entry.action === 'LOGIN' ? '✓ Success' : '✗ Failed'}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  {new Date(entry.createdAt).toLocaleDateString()}<br/>
                  {new Date(entry.createdAt).toLocaleTimeString()}
                </div>
                <div style={{ fontSize: 13 }}>{entry.ipAddress || 'Unknown'}</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>
                  {entry.details ? JSON.parse(entry.details)?.reason || '' : ''}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Sessions Tab */}
      {activeTab === 'sessions' && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24 }}>
          <h3 style={{ margin: '0 0 20px 0' }}>Active Sessions</h3>
          {sessions.map((session, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 16, background: session.current ? '#f0fdf4' : '#f9fafb', borderRadius: 8, marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {session.current && <span style={{ color: '#10b981' }}>● </span>}
                  Current Session
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  IP: {session.ip} • Last active: {new Date(session.lastActive).toLocaleString()}
                </div>
              </div>
              {!session.current && (
                <button style={{ padding: '6px 12px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>
                  Revoke
                </button>
              )}
            </div>
          ))}
          <div style={{ marginTop: 16, padding: 12, background: '#eff6ff', borderRadius: 8, fontSize: 13, color: '#1e40af' }}>
            💡 <strong>Tip:</strong> If you notice any suspicious sessions, change your password immediately.
          </div>
        </div>
      )}
    </div>
  );
}
